// src/components/EventLanding.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useWeb3React } from '@web3-react/core';
import WalletConnectButton from './wallet/WalletConnectButton';
import MetaMaskButton from './wallet/MetaMaskButton';
import CoinbaseWalletConnector from './wallet/CoinbaseWalletConnector';
import MyTickets, { clearTicketsCache } from './tickets/MyTickets';
import { loadContracts, getBrowserProvider, getReadOnlyProvider } from '../utils/blockchain';
import { formatPGT } from '../utils/blockchain';
import { useGaslessPurchase } from '../hooks/useGaslessPurchase';
import usePGTOperations from '../hooks/usePGTOperations';
import PurchaseTab from './tabs/PurchaseTab';
import { getDomain } from '../utils/eip712';
import { BrowserProvider, Contract, JsonRpcProvider } from 'ethers';
import EventTicketABI from '../abis/EventTicket.json';
import XPSystem, { awardXP } from './gamification/XPSystem';
import EventManagement from './admin/EventManagement';
import BuyPGTModalWithNative from './BuyPGTModalWithNative';
import { SupportTab, SupportLicenseGate, isSupportEnabled, getSupportConfig } from '../support';
import './EventLanding.css';
import { GAS_FEE_PER_TX, NOTIFICATION_DURATION_SHORT, NOTIFICATION_DURATION_LONG } from '../config/constants';
import EventMap from './EventMap';
import { openNavigation, geocodeAddress } from '../utils/mapUtils';

/* eslint-env es2020 */

const _BigInt = typeof globalThis.BigInt !== 'undefined' ? globalThis.BigInt : (v) => Number(v);

const EVENT_TICKET_ADDRESS = process.env.REACT_APP_EVENT_TICKET_ADDRESS;
const USE_MOCK_TICKETS = process.env.REACT_APP_MOCK_TICKETS === 'true';

const IS_DEV_MODE = process.env.NODE_ENV === 'development' || process.env.REACT_APP_DEV_MODE === 'true';
const IS_XP_ENABLED = (process.env.REACT_APP_XP_ENABLED ?? 'true') !== 'false';

const shortAddr = (a) => (a ? `${a.slice(0, 6)}…${a.slice(-4)}` : '');

// Fallback images from public/events/
const FALLBACK_IMAGES = ['/events/pregame.png', '/events/basefest.png', '/events/test.png'];
const getDefaultImage = (eventId) => {
  // Deterministic fallback based on event id hash
  const hash = String(eventId || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  return FALLBACK_IMAGES[hash % FALLBACK_IMAGES.length];
};

const MOCK_TICKET_TYPES = [
  {
    id: 1, price: 1000, maxSupply: 100, totalSupply: 12,
    saleStart: Math.floor(Date.now() / 1000) - 3600,
    saleEnd: Math.floor(Date.now() / 1000) + 30 * 86400,
    isActive: true, metadataURI: 'General Admission',
    perks: [{ id: 0, name: 'Early Entry' }, { id: 1, name: 'Free Drink' }],
  },
  {
    id: 2, price: 2500, maxSupply: 50, totalSupply: 3,
    saleStart: Math.floor(Date.now() / 1000) - 3600,
    saleEnd: Math.floor(Date.now() / 1000) + 30 * 86400,
    isActive: true, metadataURI: 'VIP Access',
    perks: [{ id: 0, name: 'Backstage Pass' }, { id: 1, name: 'Meet & Greet' }],
  },
];

// EIP-712 types must match contract's MintTicketBatch (user, itemsHash, nonce)
const TYPES_BATCH = {
  MintTicketBatch: [
    { name: 'user', type: 'address' },
    { name: 'itemsHash', type: 'bytes32' },
    { name: 'nonce', type: 'uint256' },
  ],
};

// Compute itemsHash matching contract's _hashMintItems
import { keccak256 as ethersKeccak256, AbiCoder, solidityPacked } from 'ethers';
const MINT_ITEM_TYPEHASH = ethersKeccak256(new TextEncoder().encode('MintItem(uint256 ticketTypeId,uint256 amount)'));
const _abiCoder = new AbiCoder();
function computeItemsHash(items) {
  const hashes = items.map(item => {
    const encoded = _abiCoder.encode(
      ['bytes32', 'uint256', 'uint256'],
      [MINT_ITEM_TYPEHASH, _BigInt(item.ticketTypeId), _BigInt(item.amount)]
    );
    return ethersKeccak256(encoded);
  });
  const packed = solidityPacked(hashes.map(() => 'bytes32'), hashes);
  return ethersKeccak256(packed);
}

export default function EventLanding({ event }) {
  const { account, isActive, chainId } = useWeb3React();
  const [contracts, setContracts] = useState(null); // eslint-disable-line no-unused-vars
  const [ticketTypes, setTicketTypes] = useState([]);
  const [cart, setCart] = useState({});
  const [loading, setLoading] = useState(true);
  const [ticketsRefreshKey, setTicketsRefreshKey] = useState(0);
  const [activeTab, setActiveTab] = useState('purchase');

  const [singleLoading, setSingleLoading] = useState({});
  const [singleError, setSingleError] = useState({});
  const [singleSuccess, setSingleSuccess] = useState({});
  const [notification, setNotification] = useState(null);
  const [devPurchaseLoading, setDevPurchaseLoading] = useState({});
  const [expandedTicketTypes, setExpandedTicketTypes] = useState({});
  const [walletPanelOpen, setWalletPanelOpen] = useState(false);

  const isCorrectNetwork = chainId === event.chainId;
  const wrongNetwork = isActive && !isCorrectNetwork;

  // Helper: show toast notification
  const showNotification = useCallback((type, message, txHash = null, duration = NOTIFICATION_DURATION_SHORT) => {
    setNotification({ type, message, txHash });
    setTimeout(() => setNotification(null), duration);
  }, []);

  // PGT operations (balance, allowance, approval, token purchase)
  const pgt = usePGTOperations({ account, isActive, chainId, showNotification });

  // Admin check
  const adminAddresses = useMemo(() => {
    const adminAddressesEnv = process.env.REACT_APP_ADMIN_ADDRESSES || process.env.REACT_APP_ADMIN_ADDRESS;
    return adminAddressesEnv
      ? adminAddressesEnv.split(',').map(addr => addr.trim().toLowerCase())
      : [];
  }, []);
  const isAdmin = account && adminAddresses.length > 0 && adminAddresses.includes(account.toLowerCase());

  // Gasless purchase hook
  const {
    purchaseBatch,
    loading: purchasing,
    error: purchaseError,
    success: purchaseSuccess,
  } = useGaslessPurchase({
    eventTicketAddress: event?.contractAddress || EVENT_TICKET_ADDRESS,
    chainId,
  });

  // Update balances after purchase
  useEffect(() => {
    pgt.refreshBalance();
    pgt.refreshAllowance();

    if (purchaseSuccess) {
      setTicketsRefreshKey(prev => prev + 1);
      setCart({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchaseSuccess, account]);

  // Load event data
  const loadEventData = useCallback(async () => {
    if (USE_MOCK_TICKETS) {
      setTicketTypes(MOCK_TICKET_TYPES);
      pgt.setPgtBalance(5000n);
      pgt.setAllowance(999999999n);
      setLoading(false);
      return;
    }

    try {
      const provider = window.ethereum ? getBrowserProvider() : null;
      const cs = await loadContracts(provider, { requestSigner: false });
      setContracts(cs);

      // Use event.contractAddress if it differs from the default env contract
      const eventContractAddr = event?.contractAddress || EVENT_TICKET_ADDRESS;
      let ticketContract = cs?.eventTicket;

      if (eventContractAddr && eventContractAddr.toLowerCase() !== EVENT_TICKET_ADDRESS?.toLowerCase()) {
        const roProvider = provider || getReadOnlyProvider();
        ticketContract = new Contract(eventContractAddr, EventTicketABI.abi || EventTicketABI, roProvider);
        console.log(`[EventLanding] Using event contract ${eventContractAddr} instead of default ${EVENT_TICKET_ADDRESS}`);
      }

      if (!ticketContract) {
        setTicketTypes([]);
        setLoading(false);
        return;
      }

      const types = [];
      const now = Math.floor(Date.now() / 1000);
      const ticketIdsToFetch = event?.ticketTypes?.length > 0
        ? event.ticketTypes.map(t => Number(t.id))
        : Array.from({ length: 200 }, (_, i) => i + 1);

      // EventTicketWithEvents has getTicketType() with name; legacy has only ticketTypes() auto-getter
      // ALWAYS use Base mainnet RPC — hardcoded to avoid wrong-chain issues from wallet or env fallback
      const BASE_MAINNET_RPC = process.env.REACT_APP_BASE_RPC_URL || process.env.REACT_APP_BASE_RPC
        || 'https://base-mainnet.g.alchemy.com/v2/abklby2rfDbW09WF-2pYU8Eul5wre5nq';
      const roProvider2 = new JsonRpcProvider(BASE_MAINNET_RPC);
      const enrichedContract = new Contract(eventContractAddr,
        ['function getTicketType(uint256) view returns (uint256 eventId, string name, uint256 price, uint256 maxSupply, uint256 sold, uint256 available, uint256 saleStart, uint256 saleEnd, string metadataURI)'],
        roProvider2);

      console.log(`[EventLanding] Fetching ${ticketIdsToFetch.length} ticket types:`, ticketIdsToFetch, 'from contract:', eventContractAddr);

      for (const id of ticketIdsToFetch) {
        try {
          let name = '', price, maxSupply, totalSupply = 0, saleStart, saleEnd;

          try {
            // EventTicketWithEvents.getTicketType(id) → (eventId, name, price, maxSupply, sold, available, saleStart, saleEnd, metadataURI)
            const tt = await enrichedContract.getTicketType(id);
            name = tt[1] || '';
            price = tt[2];
            maxSupply = Number(tt[3]?.toString() || '0');
            totalSupply = Number(tt[4]?.toString() || '0');
            saleStart = Number(tt[6]?.toString() || '0');
            saleEnd = Number(tt[7]?.toString() || '0');
            console.log(`[EventLanding] Ticket #${id}: ${name}, price=${price}, maxSupply=${maxSupply}`);
          } catch (enrichErr) {
            console.warn(`[EventLanding] getTicketType(${id}) failed:`, enrichErr.message);
            // Legacy fallback: ticketTypes() auto-getter
            // Returns: (price, maxSupply, saleStart, saleEnd, transferLockStart, transferLockEnd, metadataURI)
            const raw = await ticketContract.ticketTypes(id);
            price = raw[0];
            maxSupply = Number(raw[1]?.toString() || '0');
            saleStart = Number(raw[2]?.toString() || '0');
            saleEnd = Number(raw[3]?.toString() || '0');
            // raw[4]=transferLockStart, raw[5]=transferLockEnd — skip
            try { totalSupply = Number((await ticketContract.totalSupplyByType(id)).toString()); } catch {}
          }

          if (maxSupply <= 0) continue;

          const eventTicketType = event?.ticketTypes?.find(t => Number(t.id) === id);
          const displayName = eventTicketType?.name || name || `Ticket #${id}`;

          types.push({
            id,
            price: Number(price?.toString() || '0'),
            maxSupply, totalSupply, saleStart, saleEnd,
            isActive: saleStart <= now && now <= saleEnd,
            metadataURI: displayName,
            perks: [],
          });
        } catch {
          continue;
        }
      }

      setTicketTypes(types);
      setLoading(false);

      // Load PGT decimals (fallback to 2 if call fails)
      if (process.env.REACT_APP_PGT_ADDRESS && window.ethereum) {
        try {
          const roProvider = new BrowserProvider(window.ethereum);
          const pgtRO = new Contract(process.env.REACT_APP_PGT_ADDRESS, ['function decimals() view returns (uint8)'], roProvider);
          const dec = await pgtRO.decimals();
          pgt.setPgtDecimals(Number(dec));
        } catch (decErr) {
          console.warn('[EventLanding] Failed to load PGT decimals, using default (2):', decErr.message);
          pgt.setPgtDecimals(2);
        }
      }

      // Load balance & allowance
      await pgt.refreshBalance();
      await pgt.refreshAllowance();
    } catch (err) {
      console.error('Failed to load event data', err);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, event]);

  useEffect(() => {
    // Always load event data (read-only via RPC) — wallet not required to browse tickets
    loadEventData();
  }, [loadEventData]);

  // Load supported tokens
  useEffect(() => {
    if (isActive && isCorrectNetwork) {
      pgt.loadSupportedTokens(event.chainId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, isCorrectNetwork, event.chainId]);

  // Switch network
  const switchToBaseNetwork = async () => {
    if (!window.ethereum) return;
    const targetChainId = event.chainId;
    const chainIdHex = `0x${targetChainId.toString(16)}`;
    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: chainIdHex }],
      });
    } catch (switchError) {
      if (switchError.code === 4902) {
        const networkConfig = {
          8453: {
            chainName: 'Base Mainnet',
            rpcUrls: ['https://mainnet.base.org'],
            nativeCurrency: { name: 'ETH', symbol: 'ETH', decimals: 18 },
            blockExplorerUrls: ['https://basescan.org'],
          },
          84532: {
            chainName: 'Base Sepolia',
            rpcUrls: ['https://sepolia.base.org'],
            nativeCurrency: { name: 'ETH', symbol: 'ETH', decimals: 18 },
            blockExplorerUrls: ['https://sepolia.basescan.org'],
          },
        };
        const config = networkConfig[targetChainId];
        if (!config) {
          showNotification('error', 'Unsupported network');
          return;
        }
        await window.ethereum.request({
          method: 'wallet_addEthereumChain',
          params: [{ chainId: chainIdHex, ...config }],
        });
      } else {
        showNotification('error', 'Failed to switch network. Please try manually in MetaMask.');
      }
    }
  };

  const updateCart = (typeId, amount) => {
    setCart((prev) => ({ ...prev, [typeId]: Math.max(0, amount) }));
  };

  const totalCost = ticketTypes.reduce((sum, type) => {
    const amount = cart[type.id] || 0;
    return sum + type.price * amount;
  }, 0) + GAS_FEE_PER_TX;

  const hasEnoughBalance = pgt.pgtBalance >= _BigInt(totalCost);
  const isBalanceZero = pgt.pgtBalance === 0n;
  const hasItems = Object.values(cart).some((v) => v > 0);

  // Batch purchase handler
  const handlePurchase = async () => {
    if (!isActive || !account) {
      showNotification('error', 'Wallet not connected. Please connect your wallet.');
      return;
    }

    const items = Object.entries(cart)
      .filter(([, amount]) => amount > 0)
      .map(([typeId, amount]) => ({
        ticketTypeId: Number(typeId),
        amount: Number(amount),
      }));
    if (items.length === 0) return;

    const requiredBN = _BigInt(totalCost);
    const ok = await pgt.ensureAllowanceForAmount(requiredBN, false);
    if (!ok) {
      showNotification('error', 'Insufficient PGT allowance. Please approve first.');
      return;
    }

    if (!isActive || !account) {
      showNotification('error', 'Wallet disconnected. Please reconnect.');
      return;
    }

    const result = await purchaseBatch(items);
    if (result?.ok) {
      setCart({});
      clearTicketsCache(account);

      const totalTickets = items.reduce((sum, item) => sum + item.amount, 0);
      if (IS_XP_ENABLED) {
        if (totalTickets >= 3) {
          const batchBonusXP = totalTickets * 20;
          awardXP(account, 'BATCH_PURCHASE', { ticketCount: totalTickets, bonusXP: batchBonusXP });
        }
        for (let i = 0; i < totalTickets; i++) {
          awardXP(account, 'TICKET_PURCHASE', { batch: true });
        }
      }

      showNotification('success', `${totalTickets} ticket(s) minted successfully! Check "My Tickets" tab to view and add to wallet.`, result.txHash, NOTIFICATION_DURATION_LONG);
    } else if (result?.error) {
      showNotification('error', result.error);
    }
  };

  // Single purchase (direct on-chain, no server)
  const handleSinglePurchase = useCallback(
    async (ticketTypeId, amount = 1) => {
      if (!isActive || !account) {
        showNotification('error', 'Connect wallet first');
        return;
      }
      if (!isCorrectNetwork) {
        showNotification('error', 'Wrong network. Please switch network.');
        return;
      }

      try {
        const t = ticketTypes.find((x) => x.id === ticketTypeId);
        const priceBN = _BigInt((t?.price || 0) * amount);
        const ok = await pgt.ensureAllowanceForAmount(priceBN, true);
        if (!ok) {
          showNotification('error', 'Approve was not confirmed. Purchase cancelled.');
          return;
        }
      } catch (e) {
        console.warn('single ensureAllowance failed', e);
      }

      setSingleError((m) => ({ ...m, [ticketTypeId]: null }));
      setSingleSuccess((m) => ({ ...m, [ticketTypeId]: false }));
      setSingleLoading((m) => ({ ...m, [ticketTypeId]: true }));

      try {
        const provider = new BrowserProvider(window.ethereum);
        const signer = await provider.getSigner();
        const user = await signer.getAddress();

        const contractAddr = event?.contractAddress || EVENT_TICKET_ADDRESS;
        const domain = getDomain(Number(chainId), contractAddr);
        const nonceStr = String(Date.now() * 1000 + Math.floor(Math.random() * 1000));

        // Compute itemsHash matching contract
        const items = [{ ticketTypeId: Number(ticketTypeId), amount: Number(amount) }];
        const itemsHash = computeItemsHash(items);

        const value = { user, itemsHash, nonce: nonceStr };
        const signature = await signer.signTypedData(domain, TYPES_BATCH, value);

        // Call contract directly
        const eventTicketContract = new Contract(contractAddr, EventTicketABI.abi || EventTicketABI, signer);
        const mintItems = items.map(item => ({
          ticketTypeId: _BigInt(item.ticketTypeId),
          amount: _BigInt(item.amount),
        }));

        const tx = await eventTicketContract.executeMintTicketBatch(user, mintItems, nonceStr, signature);
        const receipt = await tx.wait();

        setSingleSuccess((m) => ({ ...m, [ticketTypeId]: true }));
        clearTicketsCache(account);

        let xpMessage = '';
        if (IS_XP_ENABLED) {
          const xpResult = awardXP(account, 'TICKET_PURCHASE', {
            ticketTypeId: Number(ticketTypeId),
            amount: Number(amount)
          });
          xpMessage = xpResult ? ` (+${xpResult.xpAwarded} XP)` : '';
        }
        showNotification('success', `Ticket minted successfully!${xpMessage} Check "My Tickets" tab.`, receipt.hash, NOTIFICATION_DURATION_LONG);

        try {
          await pgt.refreshBalance();
          await pgt.refreshAllowance();
        } catch {}
      } catch (e) {
        console.error('[single purchase] failed:', e);
        setSingleError((m) => ({
          ...m,
          [ticketTypeId]: e?.reason || e?.shortMessage || e?.message || 'Failed',
        }));
      } finally {
        setSingleLoading((m) => ({ ...m, [ticketTypeId]: false }));
      }
    },
    [isActive, account, isCorrectNetwork, chainId, ticketTypes, pgt, showNotification]
  );

  // DEV MODE: Direct on-chain purchase (same as single, just with dev label)
  const handleDevDirectPurchase = useCallback(
    async (ticketTypeId, amount = 1) => {
      if (!IS_DEV_MODE) return;
      if (!isActive || !account) {
        showNotification('error', 'Connect wallet first');
        return;
      }
      if (!isCorrectNetwork) {
        showNotification('error', 'Wrong network. Please switch network.');
        return;
      }

      setDevPurchaseLoading((m) => ({ ...m, [ticketTypeId]: true }));

      try {
        const t = ticketTypes.find((x) => x.id === ticketTypeId);
        if (!t) throw new Error('Ticket type not found');

        const priceBN = _BigInt(t.price * amount);
        if (pgt.allowance < priceBN) {
          const approved = await pgt.approvePGT(priceBN);
          if (!approved) throw new Error('Approval cancelled');
        }

        const provider = new BrowserProvider(window.ethereum);
        const signer = await provider.getSigner();
        const user = await signer.getAddress();
        const devContractAddr = event?.contractAddress || EVENT_TICKET_ADDRESS;
        const domain = getDomain(Number(chainId), devContractAddr);
        const nonceStr = String(Date.now() * 1000 + Math.floor(Math.random() * 1000));

        const items = [{ ticketTypeId: Number(ticketTypeId), amount: Number(amount) }];
        const itemsHash = computeItemsHash(items);
        const value = { user, itemsHash, nonce: nonceStr };
        const signature = await signer.signTypedData(domain, TYPES_BATCH, value);

        const eventTicketDev = new Contract(devContractAddr, EventTicketABI.abi || EventTicketABI, signer);
        const mintItems = items.map(item => ({
          ticketTypeId: _BigInt(item.ticketTypeId),
          amount: _BigInt(item.amount),
        }));

        const tx = await eventTicketDev.executeMintTicketBatch(user, mintItems, nonceStr, signature);
        const receipt = await tx.wait();

        clearTicketsCache(account);
        if (IS_XP_ENABLED) {
          awardXP(account, 'TICKET_PURCHASE', {
            ticketTypeId: Number(ticketTypeId),
            amount: Number(amount),
            devMode: true
          });
        }

        showNotification('success', '[DEV] Ticket purchased successfully! Check "My Tickets" tab.', receipt.hash, NOTIFICATION_DURATION_LONG);

        await pgt.refreshBalance();
        await pgt.refreshAllowance();

      } catch (e) {
        console.error('[Dev Purchase] failed:', e);
        showNotification('error', `[DEV] Purchase failed: ${e?.reason || e?.message || 'Unknown error'}`);
      } finally {
        setDevPurchaseLoading((m) => ({ ...m, [ticketTypeId]: false }));
      }
    },
    [isActive, account, isCorrectNetwork, chainId, ticketTypes, pgt, showNotification]
  );

  const toggleTicketTypeExpand = (ticketTypeId) => {
    setExpandedTicketTypes((prev) => ({
      ...prev,
      [ticketTypeId]: !prev[ticketTypeId],
    }));
  };

  const getTicketTypeInfo = (ticketTypeId) => {
    if (!event.ticketTypes) return null;
    return event.ticketTypes.find((tt) => Number(tt.id) === Number(ticketTypeId));
  };

  if (loading) {
    return (
      <div className="event-landing">
        <div className="event-bg" style={{ backgroundImage: `url(${event.image || getDefaultImage(event.id)})` }} />
        <div className="event-overlay">
          <div className="loading">Loading event...</div>
        </div>
      </div>
    );
  }

  const requiredForCartBN = _BigInt(totalCost);
  const allowanceEnoughForCart = pgt.allowance >= requiredForCartBN;

  return (
    <div className="event-landing">
      <div className="event-bg" style={{ backgroundImage: `url(${event.image || getDefaultImage(event.id)})` }} />
      <div className="event-overlay">
        {/* Notification */}
        {notification && (
          <div className={`notification ${notification.type}`}>
            <div className="notification-content">
              <p className="notification-message">{notification.message}</p>
              {notification.txHash && (
                <a
                  href={`https://${event.chainId === 8453 ? '' : 'sepolia.'}basescan.org/tx/${notification.txHash}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="notification-link"
                >
                  View transaction →
                </a>
              )}
            </div>
            <button className="notification-close" onClick={() => setNotification(null)}>✕</button>
          </div>
        )}

        <header className="event-header">
          {/* Line 1: PGT logo + Event name + wallet */}
          <div className="event-header-line1">
            <img
              src="/assets/pgt-coin.png"
              alt="PGT"
              className="event-header-logo"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <h1>{event.name}</h1>
            {isActive && isCorrectNetwork && (
              <div className="wallet-pill-wrap">
                <button
                  className={`wallet-pill ${walletPanelOpen ? 'open' : ''}`}
                  onClick={() => setWalletPanelOpen(prev => !prev)}
                >
                  <img src="/assets/pgt-coin.png" alt="" className="wallet-pill-icon" />
                  <span className="wallet-pill-addr">{shortAddr(account)}</span>
                  <span className="wallet-pill-bal">{formatPGT(Number(pgt.pgtBalance.toString()))}</span>
                  <span className="wallet-pill-arrow">{walletPanelOpen ? '▴' : '▾'}</span>
                </button>
                {walletPanelOpen && (
                  <div className="wallet-panel">
                    <div className="wallet-panel-row">
                      <span className="wallet-panel-label">Balance</span>
                      <span className="wallet-panel-value">{formatPGT(Number(pgt.pgtBalance.toString()))} PGT</span>
                    </div>
                    <div className="wallet-panel-row">
                      <span className="wallet-panel-label">Allowance</span>
                      <span className="wallet-panel-value">{formatPGT(Number(pgt.allowance.toString()))} PGT</span>
                    </div>
                    <div className="wallet-panel-divider" />
                    <div className="wallet-panel-topup">
                      <div className="wallet-panel-input-row">
                        <input
                          type="number"
                          min="1"
                          step="1"
                          value={pgt.buyUSD}
                          onChange={(e) => pgt.setBuyUSD(Math.max(1, Number(e.target.value || 0)))}
                          className="wallet-panel-input"
                          placeholder="PGT"
                        />
                        {pgt.supportedTokens.map((token) => (
                          <button
                            key={token.symbol}
                            onClick={() => pgt.buyPGTWithToken(token)}
                            disabled={pgt.buyingPGT}
                            className="wallet-panel-token-btn"
                          >
                            +{pgt.buyUSD} via {token.symbol}
                          </button>
                        ))}
                      </div>
                      <button className="wallet-panel-buy-btn" onClick={() => pgt.setShowBuyModal(true)}>
                        Buy PGT
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          {/* Line 2: Meta chips (always present, min-height reserved) */}
          <div className="event-header-line2">
            <span className="event-meta-chip">
              {new Date(event.date).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
              {', '}
              {new Date(event.date).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
            </span>
            {event.venue && (
              <span
                className="event-meta-chip event-meta-chip--venue"
                onClick={async () => {
                  const coords = event.venueCoordinates || await geocodeAddress(event.venue);
                  if (coords) openNavigation(coords.lat, coords.lng, event.venue);
                  else alert('Could not find venue location');
                }}
              >
                📍 {event.venue}
              </span>
            )}
            {event.capacity && (
              <span className="event-meta-chip">👥 {event.capacity.toLocaleString()}</span>
            )}
            {event.description && (
              <span className="event-meta-chip event-meta-chip--desc" title={event.description}>
                {event.description.length > 60 ? event.description.slice(0, 60) + '…' : event.description}
              </span>
            )}
          </div>
        </header>

        {/* Event Hero — always visible */}
        <div className="event-hero">
          <img
            src={event.image || getDefaultImage(event.id)}
            alt={event.name}
            className="event-hero-img"
            onError={(e) => {
              const fallback = getDefaultImage(event.id);
              if (!e.target.src.endsWith(fallback)) {
                e.target.src = fallback;
              }
            }}
          />
        </div>

        {event.description && (
          <p className="event-description-full">{event.description}</p>
        )}

        {/* Wallet Connect Banner */}
        {!isActive && (
          <div className="connect-banner">
            <span className="connect-banner-text">Connect wallet to purchase tickets</span>
            <div className="connect-banner-buttons">
              <MetaMaskButton />
              <CoinbaseWalletConnector />
              <WalletConnectButton />
            </div>
          </div>
        )}

        {/* Wrong Network Banner */}
        {isActive && wrongNetwork && (
          <div className="connect-banner connect-banner--warning">
            <span className="connect-banner-text">
              Switch to {event.chainId === 8453 ? 'Base Mainnet' : 'Base Sepolia'} to buy tickets
            </span>
            <button className="connect-banner-action" onClick={switchToBaseNetwork}>
              Switch Network
            </button>
          </div>
        )}

        {/* Connected — full UI */}
        {isActive && isCorrectNetwork && (
          <>
            {IS_XP_ENABLED && (
            <div className="xp-display-container">
              <XPSystem account={account} showDetails={false} />
            </div>
            )}

            {/* Tabs Navigation */}
            <div className="tabs-container" role="tablist" aria-label="Event sections">
              <button role="tab" aria-selected={activeTab === 'purchase'} aria-controls="panel-purchase" id="tab-purchase"
                className={`tab-button ${activeTab === 'purchase' ? 'active' : ''}`} onClick={() => setActiveTab('purchase')}>
                Purchase Tickets
              </button>
              <button role="tab" aria-selected={activeTab === 'tickets'} aria-controls="panel-tickets" id="tab-tickets"
                className={`tab-button ${activeTab === 'tickets' ? 'active' : ''}`} onClick={() => setActiveTab('tickets')}>
                My Tickets
              </button>
              {IS_XP_ENABLED && (
              <button role="tab" aria-selected={activeTab === 'xp'} aria-controls="panel-xp" id="tab-xp"
                className={`tab-button ${activeTab === 'xp' ? 'active' : ''}`} onClick={() => setActiveTab('xp')}>
                XP & Levels
              </button>
              )}
              <button role="tab" aria-selected={activeTab === 'map'} aria-controls="panel-map" id="tab-map"
                className={`tab-button ${activeTab === 'map' ? 'active' : ''}`} onClick={() => setActiveTab('map')}>
                Map
              </button>
              {isSupportEnabled() && (
              <button role="tab" aria-selected={activeTab === 'support'} aria-controls="panel-support" id="tab-support"
                className={`tab-button ${activeTab === 'support' ? 'active' : ''}`} onClick={() => setActiveTab('support')}>
                Support
              </button>
              )}
              {isAdmin && (
                <button role="tab" aria-selected={activeTab === 'admin'} aria-controls="panel-admin" id="tab-admin"
                  className={`tab-button ${activeTab === 'admin' ? 'active' : ''}`} onClick={() => setActiveTab('admin')}>
                  Admin
                </button>
              )}
            </div>

            {/* Purchase Tab */}
            {activeTab === 'purchase' && (
              <PurchaseTab
                event={event}
                pgtBalance={pgt.pgtBalance}
                allowance={pgt.allowance}
                isBalanceZero={isBalanceZero}
                hasEnoughBalance={hasEnoughBalance}
                ticketTypes={ticketTypes}
                cart={cart}
                updateCart={updateCart}
                hasItems={hasItems}
                totalCost={totalCost}
                handlePurchase={handlePurchase}
                purchasing={purchasing}
                purchaseError={purchaseError}
                purchaseSuccess={purchaseSuccess}
                handleSinglePurchase={handleSinglePurchase}
                handleDevDirectPurchase={handleDevDirectPurchase}
                singleLoading={singleLoading}
                singleError={singleError}
                singleSuccess={singleSuccess}
                devPurchaseLoading={devPurchaseLoading}
                IS_DEV_MODE={IS_DEV_MODE}
                supportedTokens={pgt.supportedTokens}
                buyUSD={pgt.buyUSD}
                setBuyUSD={pgt.setBuyUSD}
                buyPGTWithToken={pgt.buyPGTWithToken}
                buyingPGT={pgt.buyingPGT}
                approvePGT={pgt.approvePGT}
                approving={pgt.approving}
                allowanceEnoughForCart={allowanceEnoughForCart}
                requiredForCartBN={requiredForCartBN}
                showBuyModal={pgt.showBuyModal}
                setShowBuyModal={pgt.setShowBuyModal}
                expandedTicketTypes={expandedTicketTypes}
                toggleTicketTypeExpand={toggleTicketTypeExpand}
                getTicketTypeInfo={getTicketTypeInfo}
              />
            )}

            {/* My Tickets Tab */}
            {activeTab === 'tickets' && (
              <div className="my-tickets-section" role="tabpanel" id="panel-tickets" aria-labelledby="tab-tickets">
                <div className="event-controls compact">
                  <div className="event-info-summary">
                    <img
                      src={event.image || getDefaultImage(event.id)}
                      alt={event.name}
                      className="event-thumb"
                      onError={(e) => {
                        const fallback = getDefaultImage(event.id);
                        if (!e.target.src.endsWith(fallback)) {
                          e.target.src = fallback;
                        }
                      }}
                    />
                    <div className="event-info-text">
                      <h3>{event.name}</h3>
                      <div className="event-meta">
                        {event.date && (
                          <span className="meta-item">
                            <span className="meta-icon">📅</span>
                            {new Date(event.date).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </span>
                        )}
                        {event.venue && (
                          <span className="meta-item">
                            <span className="meta-icon">📍</span>
                            {event.venue}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <MyTickets
                  key={`tickets-${ticketsRefreshKey}`}
                  eventTicket={pgt.eventTicketRO}
                  account={account}
                />
              </div>
            )}

            {/* XP & Levels Tab */}
            {IS_XP_ENABLED && activeTab === 'xp' && (
              <div className="xp-dashboard-section" role="tabpanel" id="panel-xp" aria-labelledby="tab-xp">
                <h2 className="xp-dashboard-title">Your XP Dashboard</h2>
                <XPSystem account={account} showDetails={true} />
              </div>
            )}

            {/* Map Tab */}
            {activeTab === 'map' && (
              <div className="map-section" role="tabpanel" id="panel-map" aria-labelledby="tab-map" style={{marginTop:16}}>
                <EventMap events={[event]} singleEvent height="400px" />
              </div>
            )}

            {/* Support Tab (optional, license-gated) */}
            {isSupportEnabled() && activeTab === 'support' && (
              <SupportLicenseGate merchantWallet={account} bypass={getSupportConfig().bypassLicense || process.env.REACT_APP_LICENSE_ENABLED !== 'true'}>
                <SupportTab account={account} />
              </SupportLicenseGate>
            )}

            {/* Admin Tab */}
            {activeTab === 'admin' && isAdmin && (
              <div className="admin-section" role="tabpanel" id="panel-admin" aria-labelledby="tab-admin">
                <EventManagement />
              </div>
            )}
          </>
        )}

        {/* Buy PGT Modal — always available from wallet panel */}
        <BuyPGTModalWithNative
          isOpen={pgt.showBuyModal}
          onClose={() => pgt.setShowBuyModal(false)}
          chainId={event.chainId}
          simpleSwapAddress={process.env.REACT_APP_SIMPLE_SWAP_ADDRESS}
        />
      </div>
    </div>
  );
}
